import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Check, X, Settings2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { LeaveTypesManagerDialog } from "@/components/app/LeaveTypesManagerDialog";

const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};
const TYPE_LABEL = { off: "Day Off", absent: "Absent", half_day: "Half Day" };
const today = () => new Date().toISOString().slice(0, 10);
const spanOf = (r) => {
  const f = r.from_date || r.date;
  const t = r.to_date || r.from_date || r.date;
  return f === t ? f : `${f} → ${t}`;
};
const reqLabel = (r) => r.leave_type_label || TYPE_LABEL[r.type] || r.type;

export default function LeaveRequests() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [typesOpen, setTypesOpen] = useState(false);
  const [form, setForm] = useState({ from_date: today(), to_date: today(), leave_type: "off", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const isManager = ["super_admin", "general_manager", "property_admin", "supervisor"].includes(user?.role);
  const isSuperAdmin = user?.role === "super_admin";

  const load = async () => {
    const { data } = await api.get("/leave-requests");
    setRequests(data);
  };
  const loadTypes = async () => {
    try {
      const { data } = await api.get("/leave-types");
      setLeaveTypes(data);
      setForm((f) => (data.some((t) => t.key === f.leave_type) ? f : { ...f, leave_type: data[0]?.key || "off" }));
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); loadTypes(); }, []);

  const submit = async () => {
    if (form.to_date < form.from_date) { toast.error("To date can't be before From date"); return; }
    setSubmitting(true);
    try {
      await api.post("/leave-requests", form);
      toast.success("Request submitted");
      setOpen(false);
      setForm({ from_date: today(), to_date: today(), leave_type: leaveTypes[0]?.key || "off", note: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const decide = async (rid, status) => {
    try {
      await api.put(`/leave-requests/${rid}/decide`, { status });
      toast.success(`Request ${status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const pending = requests.filter((r) => r.status === "pending");
  const decided = requests.filter((r) => r.status !== "pending");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <span className="label-overline">Leave requests</span>
          <h3 className="font-display text-2xl font-bold tracking-tight mt-1">
            {isManager ? `${pending.length} pending approval` : "My requests"}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {isSuperAdmin && (
            <Button variant="outline" onClick={() => setTypesOpen(true)} data-testid="manage-leave-types-button">
              <Settings2 className="w-4 h-4 mr-2" /> Leave types
            </Button>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-zinc-950 hover:bg-zinc-800" data-testid="request-leave-button">
                <Plus className="w-4 h-4 mr-2" /> Request leave
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Request Leave</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>From</Label>
                    <Input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value, to_date: form.to_date < e.target.value ? e.target.value : form.to_date })} data-testid="req-from-date" />
                  </div>
                  <div>
                    <Label>To</Label>
                    <Input type="date" min={form.from_date} value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} data-testid="req-to-date" />
                  </div>
                </div>
                <div>
                  <Label>Leave type</Label>
                  <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                    <SelectTrigger data-testid="req-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {leaveTypes.map((t) => (
                        <SelectItem key={t.key} value={t.key}>
                          {t.label}{!t.is_paid ? " (Unpaid)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Reason (optional)</Label>
                  <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="req-note" />
                </div>
              </div>
              <DialogFooter><Button onClick={submit} disabled={submitting} className="bg-zinc-950" data-testid="req-submit">{submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Submit</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Pending */}
      {pending.length > 0 && (
        <div className="space-y-2">
          <span className="label-overline">Pending</span>
          {pending.map((r) => (
            <div key={r.id} className="bg-white border border-zinc-200 rounded-md p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`req-${r.id}`}>
              <div>
                <div className="font-medium">{r.user_name} · <span className="text-zinc-500 capitalize">{r.user_role.replace("_", " ")}</span></div>
                <div className="text-sm text-zinc-600 mt-0.5">
                  <span className="font-semibold">{reqLabel(r)}</span> on <span className="font-mono">{spanOf(r)}</span>
                  {r.num_days > 1 && <span className="text-zinc-400"> · {r.num_days} days</span>}
                </div>
                {r.note && <div className="text-xs text-zinc-500 mt-1">"{r.note}"</div>}
              </div>
              {isManager ? (
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => decide(r.id, "approved")} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-${r.id}`}>
                    <Check className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => decide(r.id, "rejected")} className="border-red-200 text-red-700 hover:bg-red-50" data-testid={`reject-${r.id}`}>
                    <X className="w-4 h-4 mr-1" /> Reject
                  </Button>
                </div>
              ) : (
                <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded border ${STATUS_STYLE.pending}`}>Pending</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Decided */}
      <div className="space-y-2">
        <span className="label-overline">History</span>
        {decided.length === 0 && pending.length === 0 && (
          <div className="bg-white border border-zinc-200 rounded-md p-8 text-center text-zinc-500 text-sm">No requests yet.</div>
        )}
        {decided.map((r) => (
          <div key={r.id} className="bg-white border border-zinc-200 rounded-md p-3 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              <span className="font-medium">{r.user_name}</span>
              <span className="text-zinc-500"> · {reqLabel(r)} on {spanOf(r)}</span>
              {r.decided_by_name && <span className="text-zinc-400 text-xs"> · by {r.decided_by_name}</span>}
            </div>
            <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded border ${STATUS_STYLE[r.status]}`}>{r.status}</span>
          </div>
        ))}
      </div>

      <LeaveTypesManagerDialog open={typesOpen} onOpenChange={setTypesOpen} onChanged={loadTypes} />
    </div>
  );
}
